import React, { useState, useEffect, useRef } from 'react';
import './ProductList.css';
import { productAPI } from '../../../config/product-api';
import { authAPI } from '../../../config/auth-api';
import { Plus, Globe, Trash2, ImageOff, Video, History, Share2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { filterCategoriesByAccess } from '../../../utils/categoryAccessFilter';
import CountrySelector from '../../Common/CountrySelector/CountrySelector';
import { getCountryName } from '../../../constants/constants';
import BannerSelectionModal from '../../Common/BannerSelectionModal/BannerSelectionModal';
import DeleteConfirmationModal from '../../Common/DeleteConfirmationModal/DeleteConfirmationModal';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import { eventLogAPI } from '../../../config/event-log-api';
import { useNavigate } from 'react-router-dom';
import { IS_ALFAJR } from '../../../constants/general';
import { PRODUCT_STICKER_OPTIONS } from '../../../constants/productStickers';
import { buildSeoPreviewUrl } from '../../../utils/seoHelpers';

const MAX_PRODUCT_MEDIA = 15;
const MAX_VARIANT_MEDIA = 10;

const mb = (bytes) => (bytes / 1024 / 1024).toFixed(2);

const kb = (bytes) => (bytes / 1024).toFixed(0);
const formatCompressionSize = (bytes) => bytes >= 1024 * 1024
  ? `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  : `${(bytes / 1024).toFixed(1)} KB`;
const formatDuration = (seconds) => `${Number(seconds || 0).toFixed(1)}s`;
const logCompressionResult = (scope, file, finalFile, status, reason = '', durationSec = null) => {
  const savedPct = file.size > 0 ? ((1 - (finalFile.size / file.size)) * 100).toFixed(1) : '0.0';
  console.log(`[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(finalFile.size)} | saved: ${savedPct}% | duration: ${durationSec !== null ? formatDuration(durationSec) : 'n/a'} | status: ${status}${reason ? ` | reason: ${reason}` : ''} | file: ${file.name}`);
};
const logCompressionSkip = (scope, file, reason) => {
  console.log(`[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(file.size)} | saved: 0.0% | duration: n/a | status: original kept | reason: ${reason} | file: ${file.name}`);
};
const BASE_WEIGHT_OPTIONS = [
  { label: '100 G', value: '100g' },
  { label: '200 G', value: '200g' },
  { label: '250 G', value: '250g' },
  { label: '500 G', value: '500g' },
  { label: '1 KG', value: '1kg' },
];

const normalizeChoiceTitle = (value = '') => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const weightMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*(kg|g|gm)$/i);
  if (!weightMatch) return trimmed;

  const amount = weightMatch[1];
  const unitRaw = weightMatch[2].toLowerCase();
  const normalizedUnit = unitRaw === 'kg' ? 'KG' : 'G';
  return `${amount} ${normalizedUnit}`;
};

const isWeightChoiceTitle = (value = '') => /^(\d+(?:\.\d+)?)\s*(kg|g|gm)$/i.test((value || '').trim());

const getWeightInGram = (value = '') => {
  const normalized = normalizeChoiceTitle(value);
  if (!normalized) return null;

  const match = normalized.match(/^(\d+(?:\.\d+)?)\s*(KG|G)$/i);
  if (!match) return null;

  const amount = parseFloat(match[1]);
  const unit = match[2].toUpperCase();
  if (isNaN(amount) || amount <= 0) return null;
  return unit === 'KG' ? amount * 1000 : amount;
};

const getChoiceModeFromProduct = (product = {}) => {
  const hasChoices = Array.isArray(product.choice) && product.choice.length > 0;
  if (!hasChoices) return 'none';
  if ((product.base_price || '').trim()) return 'with_price';
  return 'without_price';
};

const parseBasePrice = (basePrice = '') => {
  const match = basePrice.trim().match(/^(\d+(?:\.\d+)?)\s*\/\s*(.+)$/);
  if (!match) return { basePriceValue: '', baseOption: '' };
  const normalizedInput = match[2].trim().toLowerCase().replace(/\s+/g, '');
  const baseOption = BASE_WEIGHT_OPTIONS.find((opt) => opt.value === normalizedInput)?.value || normalizedInput;
  return {
    basePriceValue: match[1],
    baseOption: baseOption || ''
  };
};

const normalizeWithoutPriceTitle = (value = '') => {
  const normalized = normalizeChoiceTitle(value);
  return normalized.trim().replace(/\s+/g, ' ').toUpperCase();
};

const ProductList = ({ adminData, productFilter }) => {
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_PRODUCTS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_PRODUCTS);
  const canStatusChange = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.PRODUCT_STATUS_CHANGE);
  const canOpenProductEditor = canEdit || canStatusChange;
  const isStatusOnlyEditor = canStatusChange && !canEdit;
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_PRODUCTS);
  const canViewLogs = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_EVENT_LOGS);
  const canEditBanner = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_BANNER_PRODUCTS);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [availableCount, setAvailableCount] = useState(0);
  const [notAvailableCount, setNotAvailableCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    subCategory: 'all',
    imageFilter: 'all',
    stickerFilter: 'all',
    searchQuery: ''
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    ar: '',
    description: '',
    rate: '',
    barcode: '',
    uom: '',
    origin: '',
    categoryPairs: [{
      category: '', subCategory: '', subCategoriesOptions: []
    }],
    variants: [],
    choiceMode: 'none',
    basePriceValue: '',
    baseOption: '',
    choices: [],
    hasCleanAndCut: false,
    cleanAndCutTypes: [],
    sticker: ''
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [pageInput, setPageInput] = useState('');
  const [showBannerSelection, setShowBannerSelection] = useState(false);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({
    title: '',
    arabicTitle: '',
    description: '',
    uom: '',
    price: '',
    discount: '',
    barcode: '',
    category: '',
    subCategory: 'none',
    choiceMode: 'none',
    basePriceValue: '',
    baseOption: '',
    choices: [],
    hasCleanAndCut: false,
    cleanAndCutTypes: [],
    sticker: ''
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addSubCategories, setAddSubCategories] = useState([]);

  const [draggedMediaIndex, setDraggedMediaIndex] = useState(null);
  const [draggedVariantIndex, setDraggedVariantIndex] = useState(null);
  const [draggedVariantMediaIndex, setDraggedVariantMediaIndex] = useState(null);
  const [media, setMedia] = useState([]);

  const ffmpegRef = useRef(null);
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [isProcessingMedia, setIsProcessingMedia] = useState(false);
  const [generalSubCategories, setGeneralSubCategories] = useState([]);
  const [showOriginSelector, setShowOriginSelector] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchProducts = async (page = 1, search = '', filterStatus = 'all', filterCategory = 'all', filterSubCategory = 'all', filterImage = 'all', filterSticker = 'all') => {
    try {
      setLoading(true);
      setError(null);

      const response = await productAPI.fetchProducts(page, search, filterStatus, filterCategory, filterSubCategory, 50, filterImage, filterSticker);

      if (response.success) {
        setProducts(response.data.products);
        setTotalCount(response.data.totalCount);
        setAvailableCount(response.data.availableCount);
        setNotAvailableCount(response.data.notAvailableCount);

        if (response.data.categories && response.data.categories.length > 0) {
          const transformedCategories = response.data.categories.map(cat => ({
            id: cat._id,
            title: cat.title,
            subcategories: cat.subCategories ? cat.subCategories.map(sub => ({
              id: sub._id || sub,
              title: sub.title || sub,
            })) : []
          }));

          const filtered = filterCategoriesByAccess(transformedCategories);

          const categoriesForComponent = filtered.map(cat => ({
            title: cat.title,
            subCategories: cat.subcategories ? cat.subcategories.map(sub => sub.title) : []
          }));

          setCategoriesData(categoriesForComponent);
          setCategories(categoriesForComponent.map(cat => cat.title));
        }
      } else {
        setError('Failed to fetch products');
      }
    } catch (err) {
      setError('Error fetching products: ' + err.message);
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralSubCategories = async () => {
    try {
      const { categoryAPI } = await import('../../../config/category-api');
      const response = await categoryAPI.getGeneralSubcategories();
      if (response.success && response.data) {
        setGeneralSubCategories(
          response.data.map(g => ({ ...g, id: g._id }))
        );
      }
    } catch (error) {
      console.error('Error fetching general subcategories:', error);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts(currentPage, filters.searchQuery, filters.status, filters.category, filters.subCategory, filters.imageFilter, filters.stickerFilter);
  }, [currentPage]);

  useEffect(() => {
    fetchGeneralSubCategories();
  }, []);

  useEffect(() => {
    const openProductId = productFilter?.openProductId;
    if (!openProductId) return;

    const reopenProduct = async () => {
      try {
        const response = await productAPI.fetchProducts(1, openProductId, 'all', 'all', 'all', 50, 'all');
        if (response.success && response.data?.products?.length) {
          const product = response.data.products.find(
            (item) => String(item.productId) === String(openProductId)
          ) || response.data.products[0];
          if (product) {
            handleProductIdClick(product);
          }
        }
      } catch (err) {
        console.error('Failed to reopen product:', err);
      } finally {
        navigate('/dashboard', { replace: true, state: { page: 'product-list' } });
      }
    };

    reopenProduct();
  }, [productFilter?.openProductId]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterType]: value
      };

      if (filterType === 'category') {
        newFilters.subCategory = 'all';

        if (value === 'all' || value === 'uncategorized') {
          setSubCategories([]);
        } else {
          const selectedCategory = categoriesData.find(cat => cat.title === value);
          if (selectedCategory && selectedCategory.subCategories) {
            setSubCategories(['none', ...selectedCategory.subCategories]);
          } else {
            setSubCategories(['none']);
          }
        }
      }

      return newFilters;
    });
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchProducts(1, filters.searchQuery, filters.status, filters.category, filters.subCategory, filters.imageFilter, filters.stickerFilter);
  };

  const getSubCategoriesForCategory = (categoryValue) => {
    if (!categoryValue || !categoriesData || categoriesData.length === 0) {
      return [];
    }
    const selectedCategory = categoriesData.find(cat => cat.title === categoryValue);
    return selectedCategory && selectedCategory.subCategories ? selectedCategory.subCategories : [];
  };

  const handleShareProduct = (product) => {
    const domain = IS_ALFAJR ? 'fajr.qa' : 'ablemartonline.com';
    // Single first-category canonical URL (matches backend / webapp / app)
    const fromApi = (product.shareUrl || '').split('?')[0].trim();
    const url = fromApi.includes('/p/')
      ? fromApi
      : buildSeoPreviewUrl(product);

    navigator.clipboard.writeText(url)
      .then(() => {
        toast.success("Product link copied!");
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        toast.error("Failed to copy link");
      });
  };

  const handleProductIdClick = (product) => {
    if (!canOpenProductEditor) return;

    setSelectedProduct(product);

    const productImages = (product.images || []).map(img => ({
      mediaType: 'image',
      type: "existing",
      url: img.image,
      order: (img.order !== undefined && img.order !== null && !isNaN(Number(img.order)))
        ? Number(img.order)
        : null,
      thumbnail: img.thumbnail || null,
    }));

    const productVideos = IS_ALFAJR ? [] : (product.videos || []).map(vid => ({
      mediaType: 'video',
      type: "existing",
      url: vid.url,
      order: (vid.order !== undefined && vid.order !== null && !isNaN(Number(vid.order)))
        ? Number(vid.order)
        : null,
      thumbnail: vid.thumbnail || null,
    }));

    // Combine and re-order 
    const combinedMedia = [...productImages, ...productVideos]
      .sort((a, b) => {
        // null-order items sink to the end; within null group images come first
        const orderA = a.order !== null ? a.order : 9999;
        const orderB = b.order !== null ? b.order : 9999;
        if (orderA !== orderB) return orderA - orderB;
        // tie-break: image before video
        if (a.mediaType === 'image' && b.mediaType !== 'image') return -1;
        if (a.mediaType !== 'image' && b.mediaType === 'image') return 1;
        return 0;
      })
      // re-index so every item gets a clean 1-based string order
      .map((item, index) => ({
        ...item,
        order: (index + 1).toString()
      }));

    setMedia(combinedMedia);

    let categoryPairs = [];

    if (product.categories && Array.isArray(product.categories) && product.categories.length > 0) {
      categoryPairs = product.categories.map(cat => {
        const subCategoriesOptions = ['none', ...getSubCategoriesForCategory(cat.category || '')];
        return {
          category: cat.category || '',
          subCategory: cat.subCategory || 'none',
          subCategoriesOptions
        };
      });
    } else if (product.generalSubCategories && product.generalSubCategories.length > 0) {
      categoryPairs = [];
    } else {
      const subCategoriesOptions = ['none', ...getSubCategoriesForCategory(product.category || '')];
      categoryPairs = [{
        category: product.category || '',
        subCategory: product.subCategory || 'none',
        subCategoriesOptions
      }];
    }

    if (product.generalSubCategories && Array.isArray(product.generalSubCategories) && product.generalSubCategories.length > 0) {
      const genPairs = product.generalSubCategories.map(gen => ({
        category: `gen_${gen.id}`,
        subCategory: '',
        subCategoriesOptions: [],
        isGeneral: true,
        generalSubCategoryId: gen.id,
        generalSubCategoryName: gen.title
      }));
      categoryPairs = [...categoryPairs, ...genPairs];
    }

    const choiceMode = getChoiceModeFromProduct(product);
    const parsedBasePrice = parseBasePrice(product.base_price || '');
    const existingChoices = Array.isArray(product.choice) ? product.choice : [];

    const hasCleanAndCut = Array.isArray(product.cleanAndCutType) && product.cleanAndCutType.length > 0;
    const existingCleanAndCutTypes = hasCleanAndCut ? product.cleanAndCutType : [];

    setEditForm({
      name: product.name || '',
      ar: product.ar || '',
      description: product.description || '',
      rate: product.rate || '',
      barcode: product.barcode || '',
      uom: product.uom || '',
      categoryPairs,
      origin: product.origin || "",
      isAvailable: product.isAvailable ?? true,
      variants: (product.variants || []).map(v => ({
        sku: v.sku,
        price: v.price || '',
        quantityInStock: v.quantityInStock || 0,
        barcode: v.barcode || '',
        isAvailable: v.isAvailable ?? true,
        attributes: (v.attributes || []).map(a => ({ option_name: a.option_name, option_value: a.option_value })),
        media: [
          ...(v.images || []).map(img => ({
            mediaType: 'image',
            type: 'existing',
            url: typeof img === 'string' ? img : (img.image || ""),
            thumbnail: typeof img === 'string' ? '' : (img.thumbnail || ''),
            order: parseInt(img.order) || 0
          })),
          ...(v.videos || []).map(vid => ({
            mediaType: 'video',
            type: 'existing',
            url: typeof vid === 'string' ? vid : (vid.url || ""),
            thumbnail: typeof vid === 'string' ? '' : (vid.thumbnail || ''),
            order: parseInt(vid.order) || 0
          }))
        ].sort((a, b) => {
          const orderA = parseInt(a.order) || 0;
          const orderB = parseInt(b.order) || 0;
          if (orderA !== orderB) return orderA - orderB;
          // For variants without specific order, strictly put images first
          if (a.mediaType === 'image' && b.mediaType !== 'image') return -1;
          if (a.mediaType !== 'image' && b.mediaType === 'image') return 1;
          return 0;
        }).map((item, idx) => ({ ...item, order: (idx + 1).toString() }))
      })),
      choiceMode,
      basePriceValue: parsedBasePrice.basePriceValue,
      baseOption: parsedBasePrice.baseOption,
      choices: existingChoices.map((choice) => ({
        title: normalizeChoiceTitle(choice.title || '') || (choice.title || ''),
        price: choice.price || '',
        status: String(choice.status) === 'false' ? false : true
      })),
      hasCleanAndCut,
      cleanAndCutTypes: existingCleanAndCutTypes.map(c => ({
        title: c.title || '',
        price: c.price !== undefined ? Number(c.price) : 0
      })),
      sticker: product.sticker || ''
    });

    setShowEditDialog(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const syncChoicePricesByBase = (choices, basePriceValue, baseOption) => {
    const basePrice = parseFloat(basePriceValue);
    const baseWeight = getWeightInGram(baseOption);
    if (!choices.length || isNaN(basePrice) || basePrice <= 0 || !baseWeight) return choices;

    return choices.map((choice) => {
      const titleWeight = getWeightInGram(choice.title);
      if (!titleWeight) return choice;
      const computed = ((basePrice / baseWeight) * titleWeight).toFixed(3).replace(/\.?0+$/, '');
      return { ...choice, price: computed };
    });
  };

  const handleChoiceModeChange = (formType, value) => {
    if (formType === 'add') {
      const shouldSeedChoice = value !== 'none';
      setAddForm((prev) => ({
        ...prev,
        choiceMode: value,
        basePriceValue: value === 'with_price' ? prev.basePriceValue : '',
        baseOption: value === 'with_price' ? prev.baseOption : '',
        choices: value === 'none' ? [] : ((prev.choices || []).length > 0 ? prev.choices : (shouldSeedChoice ? [{ title: '', price: value === 'without_price' ? (prev.price || '') : '', status: true }] : []))
      }));
      return;
    }

    const shouldSeedChoice = value !== 'none';
    setEditForm((prev) => ({
      ...prev,
      choiceMode: value,
      basePriceValue: value === 'with_price' ? prev.basePriceValue : '',
      baseOption: value === 'with_price' ? prev.baseOption : '',
      choices: value === 'none' ? [] : ((prev.choices || []).length > 0 ? prev.choices : (shouldSeedChoice ? [{ title: '', price: '', status: true }] : []))
    }));
  };

  const handleChoiceFieldChange = (formType, index, field, value) => {
    const setForm = formType === 'add' ? setAddForm : setEditForm;
    setForm((prev) => {
      const nextChoices = [...(prev.choices || [])];
      nextChoices[index] = { ...nextChoices[index], [field]: value };
      return { ...prev, choices: nextChoices };
    });
  };

  const handleChoiceTitleBlur = (formType, index) => {
    const setForm = formType === 'add' ? setAddForm : setEditForm;
    setForm((prev) => {
      const nextChoices = [...(prev.choices || [])];
      const existing = nextChoices[index];
      const raw = existing?.title || '';
      const normalized = normalizeChoiceTitle(raw);
      if (normalized && normalized !== raw) {
        nextChoices[index] = { ...existing, title: normalized };
      }
      return { ...prev, choices: nextChoices };
    });
    const form = formType === 'add' ? addForm : editForm;
    const currentTitle = (form.choices?.[index]?.title || '').trim();
    if (form.choiceMode === 'with_price' && currentTitle && !isWeightChoiceTitle(currentTitle)) {
      toast.error('Choice title must be in KG/G format for With Price');
    }
  };

  const handleAddChoiceRow = (formType) => {
    const setForm = formType === 'add' ? setAddForm : setEditForm;
    setForm((prev) => ({
      ...prev,
      choices: [...(prev.choices || []), { title: '', price: '', status: true }]
    }));
  };

  const handleRemoveChoiceRow = (formType, index) => {
    const setForm = formType === 'add' ? setAddForm : setEditForm;
    setForm((prev) => ({
      ...prev,
      choices: (prev.choices || []).filter((_, i) => i !== index)
    }));
  };

  const handleApplyBasePrice = (formType) => {
    const setForm = formType === 'add' ? setAddForm : setEditForm;
    setForm((prev) => ({
      ...prev,
      choices: syncChoicePricesByBase(prev.choices || [], prev.basePriceValue, prev.baseOption)
    }));
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => { URL.revokeObjectURL(video.src); resolve(video.duration || 10); };
      video.onerror = () => { URL.revokeObjectURL(video.src); resolve(10); };
      video.src = URL.createObjectURL(file);
    });
  };

  const loadFFmpegIfNeeded = async () => {
    if (ffmpegRef.current?.loaded) return;

    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');

    ffmpegRef.current = new FFmpeg();
    const ffmpeg = ffmpegRef.current;

    let lastMilestone = 0;
    ffmpeg.on('progress', ({ progress }) => {
      const pct = Math.floor(progress * 100);
      if (pct >= lastMilestone + 25) {
        lastMilestone = Math.floor(pct / 25) * 25;
        console.log(`[Product Video] Compressing... ${lastMilestone}%`);
      }
    });

    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });

    setFfmpegLoaded(true);
  };

  const compressProductImage = (file, targetKbRange) => {
    const startedAt = Date.now();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          // Downscale slightly if extreme to help reach targetKB
          const maxDim = 1600;
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          let minQ = 0.3, maxQ = 0.95, q = 0.75;
          let attempts = 0;

          const iterate = () => {
            canvas.toBlob((blob) => {
              attempts++;
              const kb_res = blob.size / 1024;

              if ((kb_res >= targetKbRange[0] && kb_res <= targetKbRange[1]) || attempts >= 5) {
                return finish(blob);
              }

              if (kb_res > targetKbRange[1]) {
                maxQ = q;
                q = (minQ + maxQ) / 2;
                iterate();
              } else {
                minQ = q;
                q = (minQ + maxQ) / 2;
                iterate();
              }
            }, 'image/jpeg', q);
          };

          const finish = (blob) => {
            if (blob.size < file.size) {
              const compressed = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              logCompressionResult('Product Image', file, compressed, 'compressed', `iterations: ${attempts}`, (Date.now() - startedAt) / 1000);
              resolve(compressed);
            } else {
              logCompressionResult('Product Image', file, file, 'original kept', `already optimized; iterations: ${attempts}`, (Date.now() - startedAt) / 1000);
              resolve(file);
            }
          };

          iterate();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };


  const compressProductVideo = async (videoFile) => {
    const { fetchFile } = await import('@ffmpeg/util');
    const ffmpeg = ffmpegRef.current;

    const input = `in_${Date.now()}.mp4`;
    const output = `out_${Date.now()}.mp4`;

    const duration = await getVideoDuration(videoFile);
    await ffmpeg.writeFile(input, await fetchFile(videoFile));

    const sizeMB = videoFile.size / (1024 * 1024);
    let targetReduction = 1.0;

    if (sizeMB > 4) {
      targetReduction = 0.5; // Near 5MB -> 50%
    } else if (sizeMB > 1) {
      targetReduction = 0.7; // 1-4MB -> 30% reduction
    } else if (sizeMB > 0.5) {
      targetReduction = 0.85; // 500KB - 1MB -> 15% reduction
    } else {
      targetReduction = 0.95; // < 500KB -> minimal
    }

    const targetBytes = videoFile.size * targetReduction;
    const audioBitrateKbps = 96;
    const totalBitrateKbps = Math.floor((targetBytes * 8) / duration / 1000);
    const videoBitrateKbps = Math.max(300, totalBitrateKbps - audioBitrateKbps);

    console.log(`▶ ${mb(videoFile.size)} MB | ${duration.toFixed(1)}s | target ${(targetBytes / 1024 / 1024).toFixed(2)} MB | ${videoBitrateKbps}k video`);

    await ffmpeg.exec([
      '-i', input,
      '-c:v', 'libx264',
      '-preset', 'superfast',
      '-b:v', `${videoBitrateKbps}k`,
      '-maxrate', `${Math.floor(videoBitrateKbps * 1.5)}k`,
      '-bufsize', `${videoBitrateKbps * 2}k`,
      '-vf', 'scale=trunc(iw/2)*2:trunc(ih/2)*2',
      '-pix_fmt', 'yuv420p',
      '-movflags', 'faststart',
      '-c:a', 'aac', '-b:a', `${audioBitrateKbps}k`,
      output
    ]);

    const data = await ffmpeg.readFile(output);
    await ffmpeg.deleteFile(input);
    await ffmpeg.deleteFile(output);

    return new Blob([data.buffer], { type: 'video/mp4' });
  };

  const generateVideoThumbnail = async (videoFile) => {
    try {
      const { fetchFile } = await import('@ffmpeg/util');
      const ffmpeg = ffmpegRef.current;

      const input = `thumb_in_${Date.now()}.mp4`;
      const output = `thumb_out_${Date.now()}.mp4`;

      await ffmpeg.writeFile(input, await fetchFile(videoFile));

      await ffmpeg.exec([
        '-i', input,
        '-t', '5',
        '-vf', 'scale=200:-2,fps=15',
        '-c:v', 'libx264',
        '-preset', 'fast',
        '-profile:v', 'baseline',
        '-level', '3.0',
        '-pix_fmt', 'yuv420p',
        '-movflags', 'faststart',
        '-an',
        output
      ]);

      const data = await ffmpeg.readFile(output);
      await ffmpeg.deleteFile(input);
      await ffmpeg.deleteFile(output);

      return new Blob([data.buffer], { type: 'video/mp4' });
    } catch (error) {
      console.error('Thumbnail generation error:', error);
      throw error;
    }
  };

  const handleViewLogs = (productId) => {
    navigate('/dashboard', {
      state: {
        page: 'event-logs',
        query: productId.toString(),
        entityType: 'product',
        dateFilter: 'all_time',
        productName: selectedProduct?.name || '',
        returnTo: {
          page: 'product-list',
          openProductId: productId.toString(),
          productName: selectedProduct?.name || productId.toString(),
        },
      },
    });
  };

  const handleMediaSelect = async (e) => {
    const rawFiles = Array.from(e.target.files);
    if (rawFiles.length === 0) return;
    const selectedFiles = IS_ALFAJR
      ? rawFiles.filter(file => file.type.startsWith('image/'))
      : rawFiles;

    if (media.length + selectedFiles.length > MAX_PRODUCT_MEDIA) {
      toast.error(`Maximum ${MAX_PRODUCT_MEDIA} media files allowed`);
      e.target.value = '';
      return;
    }

    let filesToProcess = selectedFiles;

    setIsProcessingMedia(true);
    const validCompressedMedia = [];

    try {
      for (const file of filesToProcess) {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');

        if (isImage) {
          if (file.size > 1 * 1024 * 1024) {
            toast.error('Image exceeds the 1MB limit');
            continue;
          }
          let finalImage = file;
          if (file.size >= 200 * 1024) {
            let targetRange;
            if (file.size >= 700 * 1024) {
              targetRange = [400, 500];
            } else if (file.size >= 300 * 1024) {
              targetRange = [200, 300];
            } else {
              targetRange = [200, 250];
            }
            console.log(`[Product Image] Compressing: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
            finalImage = await compressProductImage(file, targetRange);
          } else {
            logCompressionSkip('Product Image', file, 'limit guard: below 200 KB threshold');
          }
          validCompressedMedia.push({ file: finalImage, type: 'image' });
        } else if (isVideo) {
          if (file.size > 5 * 1024 * 1024) {
            toast.error('Video exceeds the 5MB limit');
            continue;
          }
          await loadFFmpegIfNeeded();
          const compressionStartedAt = Date.now();
          console.log(`[Product Video] Compressing: ${file.name} (${mb(file.size)} MB)`);
          const compressedBlob = await compressProductVideo(file);
          const compressedFile = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".mp4", { type: 'video/mp4' });
          const usedCompressedVideo = compressedFile.size < file.size;
          const finalVideo = usedCompressedVideo ? compressedFile : file;
          const outputMB = finalVideo.size / (1024 * 1024);
          const rangeReason = usedCompressedVideo
            ? (outputMB <= 5 ? 'under 5MB' : 'over 5MB')
            : 'output larger or already optimized';
          logCompressionResult(
            'Product Video',
            file,
            finalVideo,
            usedCompressedVideo ? 'compressed' : 'original kept',
            rangeReason,
            (Date.now() - compressionStartedAt) / 1000
          );

          const thumbnailBlob = await generateVideoThumbnail(finalVideo);
          const thumbnailFile = new File([thumbnailBlob], `thumb_${finalVideo.name}`, { type: 'video/mp4' });
          const thumbURL = URL.createObjectURL(thumbnailFile);
          console.log(`[Product Video Thumbnail] Generated | size: ${formatCompressionSize(thumbnailFile.size)}`);

          validCompressedMedia.push({
            file: finalVideo,
            type: 'video',
            thumbnailFile: thumbnailFile,
            thumbnailUrl: thumbURL
          });
        }
      }

      if (validCompressedMedia.length > 0) {
        const newItems = validCompressedMedia.map((item, idx) => ({
          mediaType: item.type,
          type: 'new',
          file: item.file,
          url: URL.createObjectURL(item.file),
          thumbnail: item.thumbnailUrl || null,
          thumbnailFile: item.thumbnailFile || null,
          order: (media.length + idx + 1).toString()
        }));

        setMedia(prev => [...prev, ...newItems]);
      }
    } catch (err) {
      console.error('Media processing error:', err);
      toast.error('Failed to process media files');
    } finally {
      setIsProcessingMedia(false);
    }
  };

  const handleRemoveMedia = (index) => {
    setMedia(prev => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((item, i) => ({ ...item, order: (i + 1).toString() }));
    });
  };

  const handleDragStart = (index) => {
    setDraggedMediaIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();

    if (draggedMediaIndex === index) return;

    setMedia(prev => {
      const updated = [...prev];
      const draggedItem = updated[draggedMediaIndex];

      updated.splice(draggedMediaIndex, 1);
      updated.splice(index, 0, draggedItem);

      return updated.map((item, i) => ({ ...item, order: (i + 1).toString() }));
    });

    setDraggedMediaIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedMediaIndex(null);
  };

  const handleVariantMediaDragStart = (variantIndex, mediaIndex) => {
    setDraggedVariantIndex(variantIndex);
    setDraggedVariantMediaIndex(mediaIndex);
  };

  const handleVariantMediaDragOver = (e, variantIndex, mediaIndex) => {
    e.preventDefault();

    if (draggedVariantIndex === null || draggedVariantMediaIndex === null) return;
    // Only allow dragging within the same variant
    if (draggedVariantIndex !== variantIndex) return;
    if (draggedVariantMediaIndex === mediaIndex) return;

    setEditForm(prev => {
      const newVariants = [...prev.variants];
      const variant = { ...newVariants[variantIndex] };
      const updatedMedia = [...variant.media];

      const draggedItem = updatedMedia[draggedVariantMediaIndex];
      updatedMedia.splice(draggedVariantMediaIndex, 1);
      updatedMedia.splice(mediaIndex, 0, draggedItem);

      // Re-index orders
      variant.media = updatedMedia.map((m, i) => ({ ...m, order: (i + 1).toString() }));
      newVariants[variantIndex] = variant;

      return { ...prev, variants: newVariants };
    });

    setDraggedVariantMediaIndex(mediaIndex);
  };

  const handleVariantMediaDragEnd = () => {
    setDraggedVariantIndex(null);
    setDraggedVariantMediaIndex(null);
  };

  const handleCategoryPairChange = (index, field, value) => {
    setEditForm(prev => {
      const newCategoryPairs = [...prev.categoryPairs];
      newCategoryPairs[index] = {
        ...newCategoryPairs[index],
        [field]: value
      };

      if (field === "category" && value.startsWith("gen_")) {
        const genId = value.replace("gen_", "");
        const genData = generalSubCategories.find(g => g._id === genId);

        newCategoryPairs[index] = {
          category: value,
          subCategory: "",
          subCategoriesOptions: [],
          isGeneral: true,
          generalSubCategoryId: genId,
          generalSubCategoryName: genData?.title,
        };

        return { ...prev, categoryPairs: newCategoryPairs };
      }

      if (field === "category") {
        const subCategoriesOptions = ["none", ...getSubCategoriesForCategory(value)];

        newCategoryPairs[index] = {
          category: value,
          subCategory: "none",
          subCategoriesOptions,
          isGeneral: false,
          generalSubCategoryId: null,
          generalSubCategoryName: null,
        };

        return { ...prev, categoryPairs: newCategoryPairs };
      }

      if (field === "subCategory") {
        newCategoryPairs[index].subCategory = value;
        return { ...prev, categoryPairs: newCategoryPairs };
      }

      return prev
    });
  };

  const handleAddCategoryPair = () => {
    setEditForm(prev => ({
      ...prev,
      categoryPairs: [
        ...prev.categoryPairs,
        { category: '', subCategory: 'none', subCategoriesOptions: [] }
      ]
    }));
  };

  const handleRemoveCategoryPair = (index) => {
    setEditForm(prev => ({
      ...prev,
      categoryPairs: prev.categoryPairs.filter((_, i) => i !== index)
    }));
  };

  const handleAddVariant = () => {
    setEditForm(prev => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          sku: '',
          price: prev.rate || '',
          isAvailable: true,
          attributes: [{ option_name: 'color', option_value: '' }],
          media: []
        }
      ]
    }));
  };

  const handleRemoveVariant = (index) => {
    setEditForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleVariantChange = (vIndex, field, value) => {
    setEditForm(prev => {
      const newVariants = [...prev.variants];
      newVariants[vIndex] = { ...newVariants[vIndex], [field]: value };
      return { ...prev, variants: newVariants };
    });
  };

  const handleAddVariantAttribute = (vIndex) => {
    setEditForm(prev => {
      const newVariants = [...prev.variants];
      newVariants[vIndex] = {
        ...newVariants[vIndex],
        attributes: [
          ...newVariants[vIndex].attributes,
          { option_name: 'size', option_value: '' }
        ]
      };
      return { ...prev, variants: newVariants };
    });
  };

  const handleRemoveVariantAttribute = (vIndex, aIndex) => {
    setEditForm(prev => {
      const newVariants = [...prev.variants];
      newVariants[vIndex] = {
        ...newVariants[vIndex],
        attributes: newVariants[vIndex].attributes.filter((_, i) => i !== aIndex)
      };
      return { ...prev, variants: newVariants };
    });
  };

  const handleVariantAttributeChange = (vIndex, aIndex, field, value) => {
    setEditForm(prev => {
      const newVariants = [...prev.variants];
      const newAttributes = [...newVariants[vIndex].attributes];
      newAttributes[aIndex] = { ...newAttributes[aIndex], [field]: value };
      newVariants[vIndex] = {
        ...newVariants[vIndex],
        attributes: newAttributes
      };
      return { ...prev, variants: newVariants };
    });
  };

  const handleVariantMediaSelect = async (e, vIndex) => {
    const rawFiles = Array.from(e.target.files);
    if (rawFiles.length === 0) return;

    const selectedFiles = IS_ALFAJR
      ? rawFiles.filter(file => file.type.startsWith('image/'))
      : rawFiles;

    const currentCount = (editForm.variants[vIndex].media || []).length;
    if (currentCount + selectedFiles.length > MAX_VARIANT_MEDIA) {
      toast.error(`Maximum ${MAX_VARIANT_MEDIA} media files allowed for this variant`);
      e.target.value = '';
      return;
    }

    let filesToProcess = selectedFiles;

    setIsProcessingMedia(true);
    const validCompressedMedia = [];

    try {
      for (const file of filesToProcess) {
        if (file.type.startsWith('image/')) {
          if (file.size > 1 * 1024 * 1024) {
            toast.error('Image exceeds the 1MB limit');
            continue;
          }
          let finalImage = file;
          if (file.size >= 200 * 1024) {
            let targetRange;
            if (file.size >= 700 * 1024) {
              targetRange = [400, 500];
            } else if (file.size >= 300 * 1024) {
              targetRange = [200, 300];
            } else {
              targetRange = [200, 250];
            }
            console.log(`[Variant Image] Compressing: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
            finalImage = await compressProductImage(file, targetRange);
          } else {
            logCompressionSkip('Variant Image', file, 'limit guard: below 200 KB threshold');
          }
          validCompressedMedia.push({ file: finalImage, type: 'image' });
        } else if (!IS_ALFAJR && file.type.startsWith('video/')) {
          if (file.size > 5 * 1024 * 1024) { toast.error('Video exceeds the 5MB limit'); continue; }
          await loadFFmpegIfNeeded();
          const compressionStartedAt = Date.now();
          console.log(`[Variant Video] Compressing: ${file.name} (${mb(file.size)} MB)`);
          const compressedBlob = await compressProductVideo(file);
          const compressedFile = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".mp4", { type: 'video/mp4' });
          const usedCompressedVideo = compressedFile.size < file.size;
          const finalVideo = usedCompressedVideo ? compressedFile : file;
          const outputMB = finalVideo.size / (1024 * 1024);
          const rangeReason = usedCompressedVideo
            ? (outputMB <= 5 ? 'under 5MB' : 'over 5MB')
            : 'output larger or already optimized';
          logCompressionResult(
            'Variant Video',
            file,
            finalVideo,
            usedCompressedVideo ? 'compressed' : 'original kept',
            rangeReason,
            (Date.now() - compressionStartedAt) / 1000
          );
          const thumbnailBlob = await generateVideoThumbnail(finalVideo);
          const thumbnailFile = new File([thumbnailBlob], `thumb_${finalVideo.name}`, { type: 'video/mp4' });
          const thumbURL = URL.createObjectURL(thumbnailFile);
          console.log(`[Variant Video Thumbnail] Generated | size: ${formatCompressionSize(thumbnailFile.size)}`);
          validCompressedMedia.push({
            file: finalVideo,
            type: 'video',
            thumbnailFile,
            thumbnail: thumbURL
          });
        }
      }

      if (validCompressedMedia.length > 0) {
        setEditForm(prev => {
          const newVariants = [...prev.variants];
          const currentMedia = newVariants[vIndex].media || [];
          const newItems = validCompressedMedia.map((item, idx) => ({
            mediaType: item.type,
            type: 'new',
            file: item.file,
            url: URL.createObjectURL(item.file),
            thumbnail: item.thumbnail || '',
            thumbnailFile: item.thumbnailFile || null,
            order: (currentMedia.length + idx + 1).toString()
          }));
          newVariants[vIndex] = {
            ...newVariants[vIndex],
            media: [...currentMedia, ...newItems]
          };
          return { ...prev, variants: newVariants };
        });
      }
    } catch (err) {
      console.error('Variant media processing error:', err);
      toast.error('Failed to process variant media');
    } finally {
      setIsProcessingMedia(false);
      e.target.value = '';
    }
  };

  const handleRemoveVariantMedia = (vIndex, mIndex) => {
    setEditForm(prev => {
      const newVariants = [...prev.variants];
      const updatedMedia = newVariants[vIndex].media.filter((_, i) => i !== mIndex);
      newVariants[vIndex] = {
        ...newVariants[vIndex],
        media: updatedMedia.map((item, i) => ({ ...item, order: (i + 1).toString() }))
      };
      return { ...prev, variants: newVariants };
    });
  };

  const handleUpdateProduct = async () => {
    if (!selectedProduct) return;

    if (!editForm.name || editForm.name.trim() === '') {
      toast.error('Product Name is required');
      return;
    }
    if (!editForm.ar || editForm.ar.trim() === '') {
      toast.error('Arabic Name is required');
      return;
    }
    if (!editForm.rate || isNaN(editForm.rate) || parseFloat(editForm.rate) <= 0) {
      toast.error('Please enter a valid product price');
      return;
    }

    const isValid = editForm.categoryPairs.every(pair => pair.category);
    if (!isValid) {
      toast.error('Please select a category for all pairs');
      return;
    }

    if (editForm.choiceMode !== 'none') {
      if (!Array.isArray(editForm.choices) || editForm.choices.length === 0) {
        toast.error('Please add at least one choice');
        return;
      }

      const choiceTitleSet = new Set();
      for (let i = 0; i < editForm.choices.length; i++) {
        const choice = editForm.choices[i];
        const normalizedTitle = normalizeChoiceTitle(choice.title || '');
        if (!normalizedTitle) {
          toast.error(`Choice ${i + 1}: title is required`);
          return;
        }
        const key = normalizedTitle.toLowerCase();
        if (choiceTitleSet.has(key)) {
          toast.error(`Choice ${i + 1}: duplicate choice title found`);
          return;
        }
        choiceTitleSet.add(key);
        if (editForm.choiceMode === 'with_price' && !isWeightChoiceTitle(choice.title || '')) {
          toast.error(`Choice ${i + 1}: title must be in KG/G format like 500g or 1kg`);
          return;
        }
        if (editForm.choiceMode === 'with_price' && (!choice.price || isNaN(choice.price))) {
          toast.error(`Choice ${i + 1}: valid price is required`);
          return;
        }
      }

      if (editForm.choiceMode === 'with_price') {
        const baseWeight = getWeightInGram(editForm.baseOption);
        if (!editForm.basePriceValue || isNaN(editForm.basePriceValue) || parseFloat(editForm.basePriceValue) <= 0 || !baseWeight) {
          toast.error('Please set valid Base Price and Base Option');
          return;
        }
      }
    }

    if (editForm.hasCleanAndCut) {
      if (!Array.isArray(editForm.cleanAndCutTypes) || editForm.cleanAndCutTypes.length === 0) {
        toast.error('Please add at least one cleaning and cutting option');
        return;
      }
      for (let i = 0; i < editForm.cleanAndCutTypes.length; i++) {
        const item = editForm.cleanAndCutTypes[i];
        if (!item.title.trim()) {
          toast.error(`Cleaning Option ${i + 1}: title is required`);
          return;
        }
      }
    }

    try {
      setUpdateLoading(true);

      let categoriesArray = [];
      let generalSubcategoriesArray = [];

      editForm.categoryPairs.forEach(pair => {
        if (pair.isGeneral) {
          generalSubcategoriesArray.push({
            id: pair.generalSubCategoryId,
            title: pair.generalSubCategoryName
          });
        } else {
          categoriesArray.push({
            category: pair.category,
            subCategory: pair.subCategory === "none" ? "" : pair.subCategory
          });
        }
      });

      const seen = new Set();
      for (const { category, subCategory } of categoriesArray) {
        const key = category + '|' + subCategory;
        if (seen.has(key)) {
          toast.error('Same category & sub-category cannot be selected more than once');
          return;
        }
        seen.add(key);
      }

      const seenGen = new Set();
      for (const g of generalSubcategoriesArray) {
        if (seenGen.has(g.id)) {
          toast.error("Same general subcategory cannot be selected more than once");
          return;
        }
        seenGen.add(g.id);
      }

      const formData = new FormData();
      formData.append('productId', selectedProduct.productId);
      formData.append('name', editForm.name);
      formData.append('ar', editForm.ar);
      formData.append('description', editForm.description);
      formData.append('rate', editForm.rate);
      formData.append('barcode', editForm.barcode);
      formData.append('uom', editForm.uom);
      formData.append('origin', editForm.origin.toLowerCase() || "");
      formData.append('isAvailable', editForm.isAvailable);
      formData.append('sticker', editForm.sticker || '');
      formData.append('categories', JSON.stringify(categoriesArray));
      formData.append("generalSubCategories", JSON.stringify(generalSubcategoriesArray));
      formData.append('choiceMode', editForm.choiceMode);
      formData.append('base_price', editForm.choiceMode === 'with_price' ? `${editForm.basePriceValue}/${editForm.baseOption}` : '');
      formData.append('choice', JSON.stringify(
        editForm.choiceMode === 'none'
          ? []
          : (editForm.choices || []).map((choice) => ({
            title: editForm.choiceMode === 'without_price'
              ? normalizeWithoutPriceTitle(choice.title)
              : normalizeChoiceTitle(choice.title),
            price: editForm.choiceMode === 'without_price' ? String(editForm.rate) : String(choice.price),
            status: choice.status ? 'true' : 'false'
          }))
      ));
      formData.append('cleanAndCutType', JSON.stringify(
        editForm.hasCleanAndCut
          ? (editForm.cleanAndCutTypes || []).map(c => ({
              title: c.title.trim(),
              price: c.price !== '' && !isNaN(c.price) ? Number(c.price) : 0
            }))
          : []
      ));

      const imagesData = media.filter(m => m.mediaType === 'image').map(img => ({
        type: img.type,
        url: img.type === "existing" ? img.url : null,
        thumbnail: img.type === "existing" && img.thumbnail ? img.thumbnail : null,
        order: img.order.toString()
      }));

      formData.append("imagesData", JSON.stringify(imagesData));

      if (!IS_ALFAJR) {
        const videosData = media.filter(m => m.mediaType === 'video').map(vid => ({
          type: vid.type,
          url: vid.type === "existing" ? vid.url : null,
          thumbnail: vid.type === "existing" ? vid.thumbnail : null,
          order: vid.order.toString(),
          hasThumbnail: vid.type === "new" ? !!vid.thumbnailFile : !!(vid.thumbnail)
        }));
        formData.append("videosData", JSON.stringify(videosData));
      }

      media
        .filter(m => m.type === "new" && m.mediaType === 'image')
        .forEach(img => {
          formData.append("images", img.file);
        });

      if (!IS_ALFAJR) {
        media
          .filter(m => m.type === "new" && m.mediaType === 'video')
          .forEach(vid => {
            formData.append("videos", vid.file);
            if (vid.thumbnailFile) {
              formData.append("video_thumbnails", vid.thumbnailFile);
            }
          });

        const variantsData = editForm.variants.map((v, vIdx) => {
          const sku = v.sku || `${vIdx + 1}_${Date.now()}`;
          return {
            sku,
            price: v.price,
            isAvailable: v.isAvailable,
            attributes: v.attributes,
            media: v.media.map(m => ({
              mediaType: m.mediaType,
              type: m.type,
              url: m.type === 'existing' ? m.url : null,
              thumbnail: m.type === 'existing' ? (m.thumbnail || null) : null,
              order: m.order,
              hasThumbnail: m.mediaType === 'video'
                ? (m.type === 'new' ? !!m.thumbnailFile : !!m.thumbnail)
                : false
            }))
          };
        });

        if (editForm.variants.length > 0) {
          for (let i = 0; i < editForm.variants.length; i++) {
            const v = editForm.variants[i];
            if (!v.price || isNaN(v.price) || parseFloat(v.price) <= 0) {
              toast.error(`Please enter a valid price for Variant No. ${i + 1}`);
              setUpdateLoading(false);
              return;
            }
            if (!v.attributes || v.attributes.length === 0) {
              toast.error(`Please add at least one attribute to Variant No. ${i + 1}`);
              setUpdateLoading(false);
              return;
            }
            for (let attr of v.attributes) {
              if (!attr.option_value || attr.option_value.trim() === '') {
                toast.error(`Please fill in all attribute values for Variant No. ${i + 1}`);
                setUpdateLoading(false);
                return;
              }
            }
          }
        };

        formData.append("variantsData", JSON.stringify(variantsData));

        editForm.variants.forEach(v => {
          v.media.forEach(m => {
            if (m.type === 'new') {
              if (m.mediaType === 'image') {
                formData.append("variant_images", m.file);
              } else if (m.mediaType === 'video') {
                formData.append("variant_videos", m.file);
                if (m.thumbnailFile) {
                  formData.append("variant_video_thumbnails", m.thumbnailFile);
                }
              }
            }
          });
        });
      }

      const response = await productAPI.updateProduct(formData);

      if (response.success) {
        setShowEditDialog(false);
        setMedia([]);
        setSelectedProduct(null);
        setEditForm({
          name: '',
          ar: '',
          description: '',
          rate: '',
          barcode: '',
          uom: '',
          origin: '',
          categoryPairs: [{ category: '', subCategory: 'none', subCategoriesOptions: [] }],
          isAvailable: true,
          variants: [],
          choiceMode: 'none',
          basePriceValue: '',
          baseOption: '',
          choices: [],
          hasCleanAndCut: false,
          cleanAndCutTypes: [],
          sticker: ''
        });
        fetchProducts(currentPage, filters.searchQuery, filters.status, filters.category, filters.subCategory, filters.imageFilter, filters.stickerFilter);
        toast.success('Product updated successfully');
      } else {
        toast.error(response.message || 'Failed to update product');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setShowEditDialog(false);
    setSelectedProduct(null);
    setEditForm({
      name: '',
      ar: '',
      description: '',
      rate: '',
      barcode: '',
      uom: '',
      origin: '',
      categoryPairs: [{ category: '', subCategory: 'none', subCategoriesOptions: [] }],
      variants: [],
      choiceMode: 'none',
      basePriceValue: '',
      baseOption: '',
      choices: [],
      hasCleanAndCut: false,
      cleanAndCutTypes: [],
      sticker: ''
    });
    setMedia([]);
    setShowDeleteConfirm(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      setDeleteLoading(true);
      const response = await productAPI.deleteProduct(selectedProduct.productId);

      if (response.success) {
        toast.success('Product deleted successfully');
        setShowDeleteConfirm(false);
        setShowEditDialog(false);
        setSelectedProduct(null);
        fetchProducts(currentPage, filters.searchQuery, filters.status, filters.category, filters.subCategory, filters.imageFilter, filters.stickerFilter);
      } else {
        toast.error(response.msg || 'Failed to delete product');
      }
    } catch (err) {
      toast.error('Error deleting product: ' + err.message);
      console.error('Error deleting product:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAddFormChange = (field, value) => {
    setAddForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };

      if (field === 'category') {
        const selectedCategory = categoriesData.find(cat => cat.title === value);
        if (selectedCategory && selectedCategory.subCategories) {
          setAddSubCategories(['none', ...selectedCategory.subCategories]);
        } else {
          setAddSubCategories(['none']);
        }
        newForm.subCategory = 'none';
      }

      return newForm;
    });
  };

  const handleTranslateTitle = async (text, formType) => {
    if (!text || text.trim() === '') return;

    try {
      const response = await authAPI.translateText(text);
      if (response.success && response.data) {
        if (formType === 'add') {
          setAddForm(prev => ({ ...prev, arabicTitle: response.data }));
        } else {
          setEditForm(prev => ({ ...prev, ar: response.data }));
        }
      }
    } catch (error) {
      console.error('Translation failed:', error);
    }
  };

  const handleAddProduct = async () => {
    if (!addForm.title.trim()) {
      toast.error("Product title is required");
      return;
    }

    if (!addForm.arabicTitle.trim()) {
      toast.error("Arabic title is required");
      return;
    }

    if (!addForm.price || Number(addForm.price) <= 0) {
      toast.error("Valid price is required");
      return;
    }

    if (!addForm.category) {
      toast.error("Please select a category");
      return;
    }

    if (!addForm.barcode) {
      toast.error("Please select barcode");
      return;
    }

    if (addForm.choiceMode !== 'none') {
      if (!Array.isArray(addForm.choices) || addForm.choices.length === 0) {
        toast.error('Please add at least one choice');
        return;
      }

      const choiceTitleSet = new Set();
      for (let i = 0; i < addForm.choices.length; i++) {
        const choice = addForm.choices[i];
        const normalizedTitle = normalizeChoiceTitle(choice.title || '');
        if (!normalizedTitle) {
          toast.error(`Choice ${i + 1}: title is required`);
          return;
        }
        const key = normalizedTitle.toLowerCase();
        if (choiceTitleSet.has(key)) {
          toast.error(`Choice ${i + 1}: duplicate choice title found`);
          return;
        }
        choiceTitleSet.add(key);
        if (addForm.choiceMode === 'with_price' && !isWeightChoiceTitle(choice.title || '')) {
          toast.error(`Choice ${i + 1}: title must be in KG/G format like 500g or 1kg`);
          return;
        }
        if (addForm.choiceMode === 'with_price' && (!choice.price || isNaN(choice.price))) {
          toast.error(`Choice ${i + 1}: valid price is required`);
          return;
        }
      }

      if (addForm.choiceMode === 'with_price') {
        const baseWeight = getWeightInGram(addForm.baseOption);
        if (!addForm.basePriceValue || isNaN(addForm.basePriceValue) || parseFloat(addForm.basePriceValue) <= 0 || !baseWeight) {
          toast.error('Please set valid Base Price and Base Option');
          return;
        }
      }
    }

    if (addForm.hasCleanAndCut) {
      if (!Array.isArray(addForm.cleanAndCutTypes) || addForm.cleanAndCutTypes.length === 0) {
        toast.error('Please add at least one cleaning and cutting option');
        return;
      }
      for (let i = 0; i < addForm.cleanAndCutTypes.length; i++) {
        const item = addForm.cleanAndCutTypes[i];
        if (!item.title.trim()) {
          toast.error(`Cleaning Option ${i + 1}: title is required`);
          return;
        }
      }
    }

    try {
      setAddLoading(true);

      const payload = {
        name: addForm.title,
        ar: addForm.arabicTitle,
        description: addForm.description || "",
        uom: addForm.uom || "",
        rate: parseFloat(addForm.price),
        discount: addForm.discount ? parseFloat(addForm.discount) : 0,
        barcode: addForm.barcode || "",
        category: addForm.category ? addForm.category.toLowerCase() : "",
        subCategory: addForm.subCategory === "none" ? "" : addForm.subCategory.toLowerCase(),
        choiceMode: addForm.choiceMode,
        base_price: addForm.choiceMode === 'with_price' ? `${addForm.basePriceValue}/${addForm.baseOption}` : '',
        choice: addForm.choiceMode === 'none'
          ? []
          : (addForm.choices || []).map((choice) => ({
            title: addForm.choiceMode === 'without_price'
              ? normalizeWithoutPriceTitle(choice.title)
              : normalizeChoiceTitle(choice.title),
            price: addForm.choiceMode === 'without_price' ? String(addForm.price) : String(choice.price),
            status: choice.status ? 'true' : 'false'
          })),
        cleanAndCutType: addForm.hasCleanAndCut
          ? (addForm.cleanAndCutTypes || []).map(c => ({
              title: c.title.trim(),
              price: c.price !== '' && !isNaN(c.price) ? Number(c.price) : 0
            }))
          : [],
        sticker: addForm.sticker || ''
      };

      const response = await productAPI.addProduct(payload);

      if (response.success) {
        toast.success('Product added successfully');
        resetAddForm(true); // true means keep category
        fetchProducts(currentPage, filters.searchQuery, filters.status, filters.category, filters.subCategory, filters.imageFilter, filters.stickerFilter);
      } else {
        toast.error(response.msg || 'Failed to add product');
      }
    } catch (err) {
      toast.error('Error adding product: ' + err.message);
      console.error('Error adding product:', err);
    } finally {
      setAddLoading(false);
    }
  };

  const resetAddForm = (keepCategory = false) => {
    setAddForm(prev => ({
      title: '',
      arabicTitle: '',
      description: '',
      uom: '',
      price: '',
      discount: '',
      barcode: '',
      category: keepCategory ? prev.category : '',
      subCategory: keepCategory ? prev.subCategory : 'none',
      choiceMode: 'none',
      basePriceValue: '',
      baseOption: '',
      choices: [],
      hasCleanAndCut: false,
      cleanAndCutTypes: [],
      sticker: ''
    }));
    if (!keepCategory) setAddSubCategories([]);
  };

  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
    resetAddForm();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleGoButton = () => {
    const page = parseInt(pageInput);
    if (page >= 1 && page <= Math.ceil(totalCount / 50)) {
      handlePageChange(page);
      setPageInput('');
    }
  };

  const handleOriginSelect = (country) => {
    setEditForm(prev => ({
      ...prev,
      origin: country.id
    }));
    setShowOriginSelector(false);
  };

  const getCategoryDisplay = (product) => {
    if (product.categories && product.categories.length > 0) {
      const firstCategory = product.categories[0].category;
      if (product.categories.length === 1) {
        return firstCategory;
      } else {
        return `${firstCategory} + ${product.categories.length - 1}`;
      }
    }
    return product.category || '-';
  };

  const getSubCategoryDisplay = (product) => {
    if (product.categories && product.categories.length > 0) {
      const firstSubCategory = product.categories[0].subCategory;
      return firstSubCategory || '-';
    }
    return product.subCategory || '-';
  };

  const formatPrice = (rate) => {
    return `${rate.toFixed(2)} QR`;
  };

  const getAvailabilityStatus = (isAvailable) => {
    return isAvailable ? 'In Stock' : 'Out of Stock';
  };

  const getAvailabilityClass = (isAvailable) => {
    return isAvailable ? 'status-available' : 'status-unavailable';
  };

  return (
    <div className="product-list-page">
      <div className="page-header">
        <div className="page-title">
          <h1>Product List</h1>
          <p>View and manage all your products in a detailed list format</p>
        </div>

        {canCreate && (
          <button onClick={() => setShowAddDialog(true)} className="add-product-button">
            Add Product
          </button>
        )}
      </div>

      <div className="filter-section">
        <div className="filter-form">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="instock">In Stock</option>
              <option value="outofstock">Out of Stock</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              {(!adminData?.categoryAccess || adminData.categoryAccess === 'all' ||
                (Array.isArray(adminData.categoryAccess) && adminData.categoryAccess.some(item => item.category === 'uncategorized'))) &&
                <option value="uncategorized">Uncategorized</option>
              }
              {generalSubCategories.map(gen => (
                <option key={gen._id} value={gen.title}>
                  {gen.title} (G)
                </option>
              ))}
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Sub Category</label>
            <select
              value={filters.subCategory}
              onChange={(e) => handleFilterChange('subCategory', e.target.value)}
              className="filter-select"
              disabled={
                filters.category === 'all' || subCategories.length === 0 ||
                generalSubCategories.some(gen => gen.title === filters.category)
              }
            >
              <option value="all">All</option>
              <option value="none">None</option>
              {subCategories.filter(sub => sub !== 'none').map(subCategory => (
                <option key={subCategory} value={subCategory}>
                  {subCategory}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Image</label>
            <select
              value={filters.imageFilter}
              onChange={(e) => handleFilterChange('imageFilter', e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="have_image">Have Image</option>
              <option value="no_image">No Image</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Sticker</label>
            <select
              value={filters.stickerFilter}
              onChange={(e) => handleFilterChange('stickerFilter', e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="with_sticker">With Sticker</option>
              <option value="no_sticker">No Sticker</option>
              {PRODUCT_STICKER_OPTIONS.filter(opt => opt.value !== '').map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Search</label>
            <input
            
              type="text"
              placeholder="Search by name or product Id..."
              value={filters.searchQuery}
              onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
              className="filter-input"
            />
          </div>

          <button onClick={applyFilters} className="apply-filters-button">
            Apply Filters
          </button>
        </div>
      </div>

      <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number stat-no-color">{totalCount.toLocaleString()}</div>
            <div className="stat-label">Total Products</div>
          </div>
          <div className="stat-card available">
            <div className="stat-number">{availableCount.toLocaleString()}</div>
            <div className="stat-label">In Stock</div>
          </div>
          <div className="stat-card unavailable">
            <div className="stat-number">{notAvailableCount.toLocaleString()}</div>
            <div className="stat-label">Out of Stock</div>
          </div>
        </div>
      </div>

      <div className="products-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading products...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={() => fetchProducts(currentPage, filters.searchQuery, filters.status, filters.category, filters.subCategory, filters.imageFilter, filters.stickerFilter)} className="retry-button">
              Retry
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <p>No products found</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Product ID</th>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Category</th>
                    <th>Sub Category</th>                    <th>Rate</th>
                    <th>UOM</th>
                    <th>Barcode</th>
                    {filters.stickerFilter !== 'all' && filters.stickerFilter !== 'no_sticker' && <th>Sticker</th>}
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product._id}>
                      <td>
                        <span
                          className={`product-id-link ${!canOpenProductEditor ? 'disabled' : ''}`}
                          onClick={() => handleProductIdClick(product)}
                        >
                          {product.productId}
                        </span>
                      </td>
                      <td className="table-product-image">
                        {product.images && product.images.length > 0 ? (
                           <span className="image-checkmark">✅</span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="product-name">
                        <span>{product.name}</span>
                      </td>
                      <td>
                        <span className={`status ${getAvailabilityClass(product.isAvailable)}`}>
                          {getAvailabilityStatus(product.isAvailable)}
                        </span>
                      </td>
                      <td>{getCategoryDisplay(product)}</td>
                      <td>{getSubCategoryDisplay(product)}</td>
                      <td className="price">{formatPrice(product.rate)}</td>
                      <td>{product.uom}</td>
                      <td>{product.barcode}</td>
                      {filters.stickerFilter !== 'all' && filters.stickerFilter !== 'no_sticker' && (
                        <td>
                          {PRODUCT_STICKER_OPTIONS.find(opt => opt.value === product.sticker)?.label || product.sticker || '-'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="page-input-container">
                <input
                  type="number"
                  value={pageInput}
                  min="1"
                  max={Math.ceil(totalCount / 50)}
                  placeholder="Page"
                  className="page-input"
                  onChange={(e) => setPageInput(e.target.value)}
                />
                <button
                  onClick={handleGoButton}
                  className="go-button"
                >
                  Go
                </button>
              </div>

              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {Math.ceil(totalCount / 50)}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalCount / 50)}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {showAddDialog && (
        <div className="dialog-overlay">
          <div className="add-dialog">
            <div className="dialog-header">
              <h3>Add New Product</h3>
              <button onClick={handleCloseAddDialog} className="close-button">
                ×
              </button>
            </div>

            <div className="dialog-content">
              <div className="add-form-grid">
                <div className="form-group-add">
                  <label>Title </label>
                  <input
                    type="text"
                    value={addForm.title}
                    onChange={(e) => handleAddFormChange('title', e.target.value)}
                    onBlur={(e) => handleTranslateTitle(e.target.value, 'add')}
                    className="form-input"
                    disabled={addLoading}
                    placeholder="Enter product title"
                  />
                </div>

                <div className="form-group-add">
                  <label>Arabic Title </label>
                  <input
                    type="text"
                    value={addForm.arabicTitle}
                    onChange={(e) => handleAddFormChange('arabicTitle', e.target.value)}
                    className="form-input"
                    disabled={addLoading}
                    placeholder="Enter Arabic title"
                    dir="rtl"
                  />
                </div>

                <div className="form-group-add">
                  <label>Description <span className="optional-label">(Optional)</span></label>
                  <textarea
                    value={addForm.description}
                    onChange={(e) => handleAddFormChange('description', e.target.value)}
                    className="form-textarea"
                    disabled={addLoading}
                    placeholder="Enter description"
                    rows="4"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group-add">
                    <label>Category </label>
                    <select
                      value={addForm.category}
                      onChange={(e) => handleAddFormChange('category', e.target.value)}
                      className="form-select"
                      disabled={addLoading}
                    >
                      <option value="">Select Category</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-add">
                    <label>Sub Category <span className="optional-label">(Optional)</span></label>
                    <select
                      value={addForm.subCategory}
                      onChange={(e) => handleAddFormChange('subCategory', e.target.value)}
                      className="form-select"
                      disabled={addLoading || !addForm.category}
                    >
                      <option value="none">None</option>
                      {addSubCategories.filter(sub => sub !== 'none').map(subCategory => (
                        <option key={subCategory} value={subCategory}>{subCategory}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group-add">
                  <label>Barcode </label>
                  <input
                    type="text"
                    value={addForm.barcode}
                    onChange={(e) => handleAddFormChange('barcode', e.target.value)}
                    className="form-input"
                    disabled={addLoading}
                    placeholder="Enter barcode"
                  />
                </div>

                <div className="form-group-add">
                  <label>Product Sticker</label>
                  <select
                    value={addForm.sticker || ''}
                    onChange={(e) => handleAddFormChange('sticker', e.target.value)}
                    className="form-select"
                    disabled={addLoading}
                  >
                    {PRODUCT_STICKER_OPTIONS.map((option) => (
                      <option key={option.value || 'none'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row-triple">
                  <div className="form-group-add">
                    <label>UOM <span className="optional-label">(Optional)</span></label>
                    <input
                      type="text"
                      value={addForm.uom}
                      onChange={(e) => handleAddFormChange('uom', e.target.value)}
                      className="form-input"
                      disabled={addLoading}
                    />
                  </div>

                  <div className="form-group-add">
                    <label>Price </label>
                    <input
                      type="number"
                      value={addForm.price}
                      onChange={(e) => handleAddFormChange('price', e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="form-input"
                      disabled={addLoading}
                      placeholder="0"
                    />
                  </div>

                  <div className="form-group-add">
                    <label>Discount (QR) <span className="optional-label">(Optional)</span></label>
                    <input
                      type="number"
                      value={addForm.discount}
                      onChange={(e) => handleAddFormChange('discount', e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="form-input"
                      disabled={addLoading}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="choice-section">
                  <label className="choice-section-title">Choices</label>
                  <div className="choice-mode-row">
                    <label><input type="radio" name="add_choice_mode" checked={addForm.choiceMode === 'none'} onChange={() => handleChoiceModeChange('add', 'none')} /> None</label>
                    <label><input type="radio" name="add_choice_mode" checked={addForm.choiceMode === 'with_price'} onChange={() => handleChoiceModeChange('add', 'with_price')} /> With Price</label>
                    <label><input type="radio" name="add_choice_mode" checked={addForm.choiceMode === 'without_price'} onChange={() => handleChoiceModeChange('add', 'without_price')} /> Without Price</label>
                  </div>

                  {addForm.choiceMode === 'with_price' && (
                    <div className="choice-base-row">
                      <div className="form-group-add">
                        <label>Base Price (QAR)</label>
                        <input
                          type="number"
                          placeholder="Base Price (QAR)"
                          value={addForm.basePriceValue}
                          onChange={(e) => handleAddFormChange('basePriceValue', e.target.value)}
                          className="form-input"
                          disabled={addLoading}
                        />
                      </div>
                      <div className="form-group-add">
                        <label>Base Option</label>
                        <select
                          value={addForm.baseOption}
                          onChange={(e) => handleAddFormChange('baseOption', e.target.value)}
                          className="form-select"
                          disabled={addLoading}
                        >
                          <option value="">Select Option</option>
                          {BASE_WEIGHT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <button type="button" className="apply-base-button" onClick={() => handleApplyBasePrice('add')} disabled={addLoading}>Apply</button>
                    </div>
                  )}

                  {addForm.choiceMode !== 'none' && (
                    <div className="choice-list">
                      {(addForm.choices || []).map((choice, index) => (
                        <div className={`choice-item-row ${addForm.choiceMode === 'without_price' ? 'without-price-row' : ''}`} key={`add-choice-${index}`}>
                          <div className="form-group-add">
                            <label>Title</label>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="e.g. 500 g"
                              value={choice.title}
                              onChange={(e) => handleChoiceFieldChange('add', index, 'title', e.target.value)}
                              onBlur={() => handleChoiceTitleBlur('add', index)}
                              disabled={addLoading}
                            />
                          </div>
                          {addForm.choiceMode !== 'without_price' && (
                            <div className="form-group-add">
                              <label>Price</label>
                              <input
                                type="number"
                                className="form-input"
                                placeholder="Price"
                                value={choice.price}
                                onChange={(e) => handleChoiceFieldChange('add', index, 'price', e.target.value)}
                                disabled={addLoading}
                              />
                            </div>
                          )}
                          <div className="choice-status-toggle">
                            <label className="toggle-switch">
                              <input type="checkbox" checked={Boolean(choice.status)} onChange={(e) => handleChoiceFieldChange('add', index, 'status', e.target.checked)} />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                          <button type="button" className="choice-remove-btn" onClick={() => handleRemoveChoiceRow('add', index)} disabled={addLoading}>×</button>
                        </div>
                      ))}
                      <div className="add-choice-btn-container">
                        <button type="button" className="add-choice-btn" onClick={() => handleAddChoiceRow('add')} disabled={addLoading}>
                          + Add Choice
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {!addForm.hasCleanAndCut ? (
                  <div className="clean-and-cut-checkbox-only-row modal-section-full" style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '16px 0 8px 0' }}>
                    <input
                      type="checkbox"
                      id="add_has_clean_and_cut"
                      checked={false}
                      onChange={() => {
                        setAddForm(prev => ({
                          ...prev,
                          hasCleanAndCut: true,
                          cleanAndCutTypes: prev.cleanAndCutTypes.length > 0 ? prev.cleanAndCutTypes : [{ title: '', price: 0 }]
                        }));
                      }}
                      disabled={addLoading}
                    />
                    <label htmlFor="add_has_clean_and_cut" style={{ fontWeight: '600', cursor: 'pointer' }}>
                      Has Cleaning & Cutting Options
                    </label>
                  </div>
                ) : (
                  <div className="choice-section modal-section-full" style={{ marginTop: '16px' }}>
                    <div className="clean-and-cut-checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <input
                        type="checkbox"
                        id="add_has_clean_and_cut"
                        checked={true}
                        onChange={() => {
                          setAddForm(prev => ({
                            ...prev,
                            hasCleanAndCut: false
                          }));
                        }}
                        disabled={addLoading}
                      />
                      <label htmlFor="add_has_clean_and_cut" style={{ fontWeight: '600', cursor: 'pointer' }}>
                        Has Cleaning & Cutting Options
                      </label>
                    </div>

                    <div className="choice-list" style={{ marginTop: '12px' }}>
                      {(addForm.cleanAndCutTypes || []).map((item, index) => (
                        <div className="choice-item-row" key={`add-clean-${index}`}>
                          <div className="form-group-add">
                            <label>Cleaning Option Title</label>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="e.g. Full Cleaning and cutting"
                              value={item.title}
                              onChange={(e) => {
                                const newTypes = [...addForm.cleanAndCutTypes];
                                newTypes[index] = { ...newTypes[index], title: e.target.value };
                                setAddForm(prev => ({ ...prev, cleanAndCutTypes: newTypes }));
                              }}
                              disabled={addLoading}
                            />
                          </div>
                          <div className="form-group-add">
                            <label>Price</label>
                            <input
                              type="number"
                              className="form-input"
                              placeholder="0"
                              value={item.price}
                              onChange={(e) => {
                                const newTypes = [...addForm.cleanAndCutTypes];
                                newTypes[index] = { ...newTypes[index], price: e.target.value };
                                setAddForm(prev => ({ ...prev, cleanAndCutTypes: newTypes }));
                              }}
                              disabled={addLoading}
                            />
                          </div>
                          <button
                            type="button"
                            className="choice-remove-btn"
                            onClick={() => {
                              setAddForm(prev => ({
                                ...prev,
                                cleanAndCutTypes: prev.cleanAndCutTypes.filter((_, i) => i !== index)
                              }));
                            }}
                            disabled={addLoading}
                            style={{ alignSelf: 'flex-end', marginBottom: '8px' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <div className="add-choice-btn-container">
                        <button
                          type="button"
                          className="add-choice-btn"
                          onClick={() => {
                            setAddForm(prev => ({
                              ...prev,
                              cleanAndCutTypes: [...prev.cleanAndCutTypes, { title: '', price: 0 }]
                            }));
                          }}
                          disabled={addLoading}
                        >
                          + Add Cleaning Option
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="dialog-footer">
              <button
                onClick={handleCloseAddDialog}
                className="cancel-button"
                disabled={addLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleAddProduct}
                className="add-button-primary"
                disabled={addLoading || !addForm.title.trim() || !addForm.arabicTitle.trim() || !addForm.price || Number(addForm.price) <= 0 || !addForm.category}
              >
                {addLoading ? 'Adding...' : 'Add New Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditDialog && selectedProduct && (
        <div className="dialog-overlay">
          <div className="edit-dialog">
            <div className="dialog-header product-edit-header">
              <div className="header-main-info">
                <div className="header-text-group">
                  <h3>Edit Product - {selectedProduct.name}</h3>
                  <div className="product-meta-header-row">
                    <p><strong>Product ID:</strong> {selectedProduct.productId}</p>
                    {isStatusOnlyEditor && (
                      <p className="header-price-display">
                        <strong>Price:</strong> {editForm.rate} QAR
                      </p>
                    )}
                  </div>
                </div>
                <div className="modal-header-actions">
                  <button
                    type="button"
                    onClick={() => handleShareProduct(selectedProduct)}
                    className="product-modal-share-btn"
                    title="Copy deep link"
                  >
                    <Share2 size={14} />
                    Share Product
                  </button>
                  {canEditBanner && (
                    <button
                      type="button"
                      onClick={() => setShowBannerSelection(true)}
                      className="banner-assignment-btn"
                    >
                      Banner with this product
                    </button>
                  )}
                </div>
              </div>

              <div className="availability-toggle-container header-status-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={editForm.isAvailable}
                    onChange={(e) => setEditForm(prev => ({ ...prev, isAvailable: e.target.checked }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className={`availability-text ${editForm.isAvailable ? 'in-stock' : 'out-of-stock'}`}>
                  {editForm.isAvailable ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>
              <button onClick={handleCloseDialog} className="close-button header-close">
                ×
              </button>
            </div>

            <div className="dialog-content">
              <div className="image-upload-section">
                <span className="section-label">
                  Product Media {!isStatusOnlyEditor && (IS_ALFAJR ? '(image max size 1mb)' : '(image max size 1mb and video max size 5 mb)')}
                </span>
                <div className="image-grid">
                  {!isStatusOnlyEditor && (
                    <label className={`add-image-btn ${isProcessingMedia ? 'compressing' : ''}`}>
                      <input
                        type="file"
                        multiple
                        accept={IS_ALFAJR ? "image/*" : "image/*,video/*"}
                        onChange={handleMediaSelect}
                        style={{ display: 'none' }}
                        disabled={updateLoading || isProcessingMedia}
                      />
                      {isProcessingMedia ? (
                        <div className="compress-loader"></div>
                      ) : (
                        <Plus size={24} />
                      )}
                      <span>{isProcessingMedia ? 'Processing...' : 'Add Media'}</span>
                    </label>
                  )}

                  {media.map((item, index) => (
                    <div
                      key={index}
                      className={`image-item ${draggedMediaIndex === index ? 'dragging' : ''}`}
                      draggable={!isProcessingMedia && !isStatusOnlyEditor}
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      {item.mediaType === 'video' ? (
                        <div className="video-preview-wrapper" onClick={() => window.open(item.url, '_blank')}>
                          <video src={item.thumbnail || item.url} muted playsInline preload="metadata" />
                          <div className="media-play-overlay">
                            <Video size={24} color="#fff" fill="#fff" />
                          </div>
                        </div>
                      ) : (
                        <img
                          src={item.thumbnail || item.url}
                          alt={`Product ${index}`}
                          onClick={() => window.open(item.url, '_blank')}
                          title="Click to view full image"
                        />
                      )}

                      {!isStatusOnlyEditor && (
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => handleRemoveMedia(index)}
                          disabled={updateLoading || isProcessingMedia}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {media.length === 0 && (
                    <div className={`no-media-placeholder ${isStatusOnlyEditor ? 'no-media-placeholder-full' : ''}`}>
                      <p>No media available for this product</p>
                    </div>
                  )}
                </div>
              </div>

              {!isStatusOnlyEditor && (
                <div className="edit-form-grid" style={{ marginBottom: '24px' }}>
                  <div className="form-group-add">
                    <label>Title</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => handleEditFormChange('name', e.target.value)}
                      onBlur={(e) => handleTranslateTitle(e.target.value, 'edit')}
                      className="form-input"
                      disabled={updateLoading}
                      placeholder="Enter product title"
                    />
                  </div>

                  <div className="form-group-add">
                    <label>Arabic Title</label>
                    <input
                      type="text"
                      value={editForm.ar}
                      onChange={(e) => handleEditFormChange('ar', e.target.value)}
                      className="form-input"
                      disabled={updateLoading}
                      placeholder="Enter Arabic title"
                      dir="rtl"
                    />
                  </div>

                  <div className="form-group-add modal-section-full">
                    <label>Description (Optional)</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => handleEditFormChange('description', e.target.value)}
                      className="form-textarea"
                      disabled={updateLoading}
                      placeholder="Enter description"
                      rows="3"
                    />
                  </div>

                  <div className="form-group-add">
                    <label>Barcode</label>
                    <input
                      type="text"
                      value={editForm.barcode}
                      onChange={(e) => handleEditFormChange('barcode', e.target.value)}
                      className="form-input"
                      disabled={updateLoading}
                      placeholder="Enter barcode"
                    />
                  </div>

                  <div className="form-group-add">
                    <label>UOM (Optional)</label>
                    <input
                      type="text"
                      value={editForm.uom}
                      onChange={(e) => handleEditFormChange('uom', e.target.value)}
                      className="form-input"
                      disabled={updateLoading}
                      placeholder="e.g. KG, Pack"
                    />
                  </div>

                  <div className="form-group-add">
                    <label>Price</label>
                    <input
                      type="number"
                      value={editForm.rate}
                      onChange={(e) => handleEditFormChange('rate', e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="form-input"
                      disabled={updateLoading}
                      placeholder="0"
                    />
                  </div>
                </div>
              )}

              {!isStatusOnlyEditor && (
                <>
                  <div className="form-group origin-selector-group" style={{ marginBottom: '32px' }}>
                    <label>Country of Origin (Optional)</label>
                    <div className="origin-button-wrapper">
                      <button
                        type="button"
                        className="choose-country-btn"
                        onClick={() => setShowOriginSelector(true)}
                        disabled={updateLoading}
                      >
                        <Globe size={20} />
                        <span>{editForm.origin ? getCountryName(editForm.origin) : "Choose Country"}</span>
                      </button>
                    </div>
                  </div>

              <div className="form-group-add" style={{ marginBottom: '24px' }}>
                <label>Product Sticker</label>
                <select
                  value={editForm.sticker || ''}
                  onChange={(e) => handleEditFormChange('sticker', e.target.value)}
                  className="form-select"
                  disabled={updateLoading}
                >
                  {PRODUCT_STICKER_OPTIONS.map((option) => (
                    <option key={option.value || 'none'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

                  <div className="category-pair-container">
                    {editForm.categoryPairs.map((pair, index) => (
                      <div key={index} className="category-pair-wrapper">
                        <div className="category-pair-row">
                          <div className="form-group-inline">
                            <label>Category</label>
                            <select
                              value={pair.category}
                              onChange={(e) => handleCategoryPairChange(index, 'category', e.target.value)}
                              className="form-select"
                              disabled={updateLoading}
                            >
                              <option value="">Select Category</option>
                              {generalSubCategories.length > 0 && (
                                <>
                                  {generalSubCategories.map(gen => (
                                    <option key={gen._id} value={`gen_${gen._id}`}>
                                      {gen.title} (G)
                                    </option>
                                  ))}
                                </>
                              )}
                              {categories.map(category => (
                                <option key={category} value={category}>{category}</option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group-inline">
                            <label>Sub Category</label>
                            <select
                              value={pair.subCategory}
                              onChange={(e) => handleCategoryPairChange(index, 'subCategory', e.target.value)}
                              className="form-select"
                              disabled={updateLoading || !pair.category || pair.isGeneral}
                            >
                              <option value="none">None</option>
                              {pair.subCategoriesOptions.filter(sub => sub !== 'none').map(subCategory => (
                                <option key={subCategory} value={subCategory}>{subCategory}</option>
                              ))}
                            </select>
                          </div>

                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveCategoryPair(index)}
                              className="remove-pair-button"
                              disabled={updateLoading}
                              title="Remove this category pair"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    <div className="add-pair-container">
                      <button
                        type="button"
                        onClick={handleAddCategoryPair}
                        className="add-pair-button"
                        disabled={updateLoading}
                      >
                        <Plus size={18} />
                        Add Another Category
                      </button>
                    </div>
                  </div>

                  <div className="section-divider"></div>
                </>
              )}

              {(!isStatusOnlyEditor || editForm.choiceMode !== 'none') && (
                <div className="choice-section modal-section-full">
                  <label className="choice-section-title">Choices</label>
                  <div className="choice-mode-row">
                    <label><input type="radio" name="edit_choice_mode" disabled={isStatusOnlyEditor} checked={editForm.choiceMode === 'none'} onChange={() => handleChoiceModeChange('edit', 'none')} /> None</label>
                    <label><input type="radio" name="edit_choice_mode" disabled={isStatusOnlyEditor} checked={editForm.choiceMode === 'with_price'} onChange={() => handleChoiceModeChange('edit', 'with_price')} /> With Price</label>
                    <label><input type="radio" name="edit_choice_mode" disabled={isStatusOnlyEditor} checked={editForm.choiceMode === 'without_price'} onChange={() => handleChoiceModeChange('edit', 'without_price')} /> Without Price</label>
                  </div>

                  {editForm.choiceMode === 'with_price' && (
                    <div className="choice-base-row">
                      <div className="form-group-add">
                        <label>Base Price (QAR)</label>
                        <input
                          type="number"
                          placeholder="Base Price (QAR)"
                          value={editForm.basePriceValue}
                          onChange={(e) => handleEditFormChange('basePriceValue', e.target.value)}
                          className="form-input"
                          disabled={updateLoading || isStatusOnlyEditor}
                        />
                      </div>
                      <div className="form-group-add">
                        <label>Base Option</label>
                        <select
                          value={editForm.baseOption}
                          onChange={(e) => handleEditFormChange('baseOption', e.target.value)}
                          className="form-select"
                          disabled={updateLoading || isStatusOnlyEditor}
                        >
                          <option value="">Select Option</option>
                          {BASE_WEIGHT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <button type="button" className="apply-base-button" onClick={() => handleApplyBasePrice('edit')} disabled={updateLoading || isStatusOnlyEditor}>Apply</button>
                    </div>
                  )}

                  {editForm.choiceMode !== 'none' && (
                    <div className="choice-list">
                      {(editForm.choices || []).map((choice, index) => (
                        <div className={`choice-item-row ${editForm.choiceMode === 'without_price' ? 'without-price-row' : ''}`} key={`edit-choice-${index}`}>
                          <div className="form-group-add">
                            <label>Title</label>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="e.g. 500 g"
                              value={choice.title}
                              onChange={(e) => handleChoiceFieldChange('edit', index, 'title', e.target.value)}
                              onBlur={() => handleChoiceTitleBlur('edit', index)}
                              disabled={updateLoading || isStatusOnlyEditor}
                            />
                          </div>
                          {editForm.choiceMode !== 'without_price' && (
                            <div className="form-group-add">
                              <label>Price</label>
                              <input
                                type="number"
                                className="form-input"
                                placeholder="Price"
                                value={choice.price}
                                onChange={(e) => handleChoiceFieldChange('edit', index, 'price', e.target.value)}
                                disabled={updateLoading || isStatusOnlyEditor}
                              />
                            </div>
                          )}
                          <div className="choice-status-toggle">
                            <label className="toggle-switch">
                              <input type="checkbox" checked={Boolean(choice.status)} onChange={(e) => handleChoiceFieldChange('edit', index, 'status', e.target.checked)} />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                          {!isStatusOnlyEditor && (
                            <button type="button" className="choice-remove-btn" onClick={() => handleRemoveChoiceRow('edit', index)} disabled={updateLoading}>×</button>
                          )}
                        </div>
                      ))}
                      {!isStatusOnlyEditor && (
                        <div className="add-choice-btn-container">
                          <button type="button" className="add-choice-btn" onClick={() => handleAddChoiceRow('edit')} disabled={updateLoading}>
                            + Add Choice
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!isStatusOnlyEditor && (
                <>
                  <div className="section-divider"></div>

                  {!editForm.hasCleanAndCut ? (
                    <div className="clean-and-cut-checkbox-only-row modal-section-full" style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '16px 0 8px 0' }}>
                      <input
                        type="checkbox"
                        id="edit_has_clean_and_cut"
                        checked={false}
                        onChange={() => {
                          setEditForm(prev => ({
                            ...prev,
                            hasCleanAndCut: true,
                            cleanAndCutTypes: prev.cleanAndCutTypes.length > 0 ? prev.cleanAndCutTypes : [{ title: '', price: 0 }]
                          }));
                        }}
                        disabled={updateLoading}
                      />
                      <label htmlFor="edit_has_clean_and_cut" style={{ fontWeight: '600', cursor: 'pointer' }}>
                        Has Cleaning & Cutting Options
                      </label>
                    </div>
                  ) : (
                    <div className="choice-section modal-section-full" style={{ marginTop: '16px' }}>
                      <div className="clean-and-cut-checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <input
                          type="checkbox"
                          id="edit_has_clean_and_cut"
                          checked={true}
                          onChange={() => {
                            setEditForm(prev => ({
                              ...prev,
                              hasCleanAndCut: false
                            }));
                          }}
                          disabled={updateLoading}
                        />
                        <label htmlFor="edit_has_clean_and_cut" style={{ fontWeight: '600', cursor: 'pointer' }}>
                          Has Cleaning & Cutting Options
                        </label>
                      </div>

                      <div className="choice-list" style={{ marginTop: '12px' }}>
                        {(editForm.cleanAndCutTypes || []).map((item, index) => (
                          <div className="choice-item-row" key={`edit-clean-${index}`}>
                            <div className="form-group-add">
                              <label>Cleaning Option Title</label>
                              <input
                                type="text"
                                className="form-input"
                                placeholder="e.g. Full Cleaning and cutting"
                                value={item.title}
                                onChange={(e) => {
                                  const newTypes = [...editForm.cleanAndCutTypes];
                                  newTypes[index] = { ...newTypes[index], title: e.target.value };
                                  setEditForm(prev => ({ ...prev, cleanAndCutTypes: newTypes }));
                                }}
                                disabled={updateLoading}
                              />
                            </div>
                            <div className="form-group-add">
                              <label>Price</label>
                              <input
                                type="number"
                                className="form-input"
                                placeholder="0"
                                value={item.price}
                                onChange={(e) => {
                                  const newTypes = [...editForm.cleanAndCutTypes];
                                  newTypes[index] = { ...newTypes[index], price: e.target.value };
                                  setEditForm(prev => ({ ...prev, cleanAndCutTypes: newTypes }));
                                }}
                                disabled={updateLoading}
                              />
                            </div>
                            <button
                              type="button"
                              className="choice-remove-btn"
                              onClick={() => {
                                setEditForm(prev => ({
                                  ...prev,
                                  cleanAndCutTypes: prev.cleanAndCutTypes.filter((_, i) => i !== index)
                                }));
                              }}
                              disabled={updateLoading}
                              style={{ alignSelf: 'flex-end', marginBottom: '8px' }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <div className="add-choice-btn-container">
                          <button
                            type="button"
                            className="add-choice-btn"
                            onClick={() => {
                              setEditForm(prev => ({
                                ...prev,
                                cleanAndCutTypes: [...prev.cleanAndCutTypes, { title: '', price: 0 }]
                              }));
                            }}
                            disabled={updateLoading}
                          >
                            + Add Cleaning Option
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Product Variants Section */}
              {!IS_ALFAJR && (!isStatusOnlyEditor || editForm.variants.length > 0) && (
                <>
                  <div className="product-variants-section">
                    {editForm.variants.map((variant, vIdx) => (
                      <div key={vIdx} className="variant-card">
                        <div className="variant-card-header">
                          <h4>Variant No. {vIdx + 1}</h4>
                          {!isStatusOnlyEditor && (
                            <button
                              type="button"
                              className="variant-remove-btn"
                              onClick={() => handleRemoveVariant(vIdx)}
                              disabled={updateLoading}
                            >
                              <Trash2 size={18} /> Remove
                            </button>
                          )}
                        </div>

                        <div className="variant-row-fields">
                          <div className="form-group-add price-group">
                            <label>Price *</label>
                            <input
                              type="number"
                              value={variant.price}
                              onChange={(e) => handleVariantChange(vIdx, 'price', e.target.value)}
                              className="form-input"
                              placeholder="Price"
                              disabled={isStatusOnlyEditor}
                            />
                          </div>
                          <div className="variant-status-item">
                            <label>Status</label>
                            <div className="availability-toggle-container">
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={variant.isAvailable}
                                  onChange={(e) =>
                                    handleVariantChange(vIdx, "isAvailable", e.target.checked)
                                  }
                                />
                                <span className="toggle-slider"></span>
                              </label>
                              <span className={`availability-text ${variant.isAvailable ? 'in-stock' : 'out-of-stock'}`}>
                                {variant.isAvailable ? 'In Stock' : 'Out of Stock'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="variant-attributes-section">
                          <div className="variant-attributes-header">
                            <div className="attr-header-label">Attribute Key</div>
                            <div className="attr-header-label">Attribute Value</div>
                          </div>
                          {variant.attributes.map((attr, aIdx) => (
                            <div key={aIdx} className="attribute-row">
                              <div className="attr-field-group">
                                <select
                                  value={attr.option_name}
                                  onChange={(e) => handleVariantAttributeChange(vIdx, aIdx, 'option_name', e.target.value)}
                                  className="form-select attr-select"
                                  disabled={isStatusOnlyEditor}
                                >
                                  <option value="color">Color</option>
                                  <option value="size">Size</option>
                                  <option value="weight">Weight</option>
                                </select>
                              </div>
                              <div className="attr-field-group">
                                <input
                                  type="text"
                                  value={attr.option_value}
                                  onChange={(e) => handleVariantAttributeChange(vIdx, aIdx, 'option_value', e.target.value)}
                                  className="form-input attr-value"
                                  placeholder="e.g. Red, Large"
                                  disabled={isStatusOnlyEditor}
                                />
                              </div>
                              {!isStatusOnlyEditor && (
                                <button
                                  type="button"
                                  className="attr-remove-btn"
                                  onClick={() => handleRemoveVariantAttribute(vIdx, aIdx)}
                                  title="Remove attribute"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))}
                          {!isStatusOnlyEditor && (
                            <div className="add-attr-container">
                              <button
                                type="button"
                                className="add-attr-btn"
                                onClick={() => handleAddVariantAttribute(vIdx)}
                              >
                                <Plus size={16} /> Add Attribute
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="variant-media-section">
                          <label>Variant Media</label>
                          <div className="variant-media-grid">
                            {!isStatusOnlyEditor && (
                              <label className={`variant-media-add ${isProcessingMedia ? 'loading' : ''}`}>
                                <input
                                  type="file"
                                  multiple
                                  accept={IS_ALFAJR ? "image/*" : "image/*,video/*"}
                                  onChange={(e) => handleVariantMediaSelect(e, vIdx)}
                                  style={{ display: 'none' }}
                                  disabled={isProcessingMedia}
                                />
                                {isProcessingMedia ? <div className="spinner-small"></div> : <Plus size={20} />}
                              </label>
                            )}

                            {variant.media.map((m, mIdx) => (
                              <div key={mIdx} className="variant-media-item">
                                {m.mediaType === 'image' ? (
                                  <img
                                    src={m.thumbnail || m.url}
                                    alt="Variant"
                                    onClick={() => window.open(m.url, '_blank')}
                                    title="Click to view full image"
                                  />
                                ) : (
                                  <div className="variant-video-wrapper" onClick={() => window.open(m.url, '_blank')}>
                                    <video src={m.thumbnail || m.url} muted playsInline preload="metadata" />
                                    <div className="media-play-overlay small">
                                      <Video size={18} color="#fff" fill="#fff" />
                                    </div>
                                  </div>
                                )}
                                {!isStatusOnlyEditor && (
                                  <button
                                    type="button"
                                    className="variant-media-remove"
                                    onClick={() => handleRemoveVariantMedia(vIdx, mIdx)}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>
                    ))}

                    {!isStatusOnlyEditor && (
                      <div className="add-variant-container">
                        <button
                          type="button"
                          className="add-variant-btn-bottom"
                          onClick={handleAddVariant}
                          disabled={updateLoading}
                        >
                          <Plus size={18} /> Add New Variant
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="dialog-footer">
              <div className="footer-left-btns">
                {canDelete && (
                  <button
                    onClick={handleDeleteClick}
                    className="delete-product-btn"
                    disabled={updateLoading || deleteLoading}
                    title="Delete this product"
                  >
                    <Trash2 size={18} />
                    Delete Product
                  </button>
                )}
                {canViewLogs && (
                  <button
                    type="button"
                    onClick={() => handleViewLogs(selectedProduct.productId)}
                    className="view-logs-btn"
                    title="View Product History"
                  >
                    <History size={16} />
                    View Logs
                  </button>
                )}
              </div>
              <div className="footer-right-btns">
                <button
                  onClick={handleCloseDialog}
                  className="cancel-button"
                  disabled={updateLoading || deleteLoading}
                >
                  Cancel
                </button>
                {canOpenProductEditor && (
                  <button
                    onClick={handleUpdateProduct}
                    className="update-button"
                    disabled={updateLoading || deleteLoading || isProcessingMedia}
                  >
                    {isProcessingMedia ? 'Processing Media...' : updateLoading ? 'Updating...' : isStatusOnlyEditor ? 'Update Status' : 'Update Product'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      <CountrySelector
        isOpen={showOriginSelector}
        onClose={() => setShowOriginSelector(false)}
        onSelect={handleOriginSelect}
        selectedCountryId={editForm.origin}
        title="Select Country of Origin"
      />

      <BannerSelectionModal
        isOpen={showBannerSelection}
        onClose={() => setShowBannerSelection(false)}
        type="product"
        targetData={{ productId: selectedProduct ? selectedProduct.productId : null }}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteProduct}
        title="Delete Product"
        message={`Are you sure you want to delete "${selectedProduct?.name}"? This action cannot be undone.`}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default ProductList;